import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { Card } from './CaseUiPrimitives.js';
import { saveUnitCadastre, type UnitCadastreRule } from '../lib/unit-cadastre-api.js';

/**
 * Krok B1c bod 9 — FORMULAR PRO VYSLEDEK NAHLIZENI DO KN NA JEDNOTKU.
 *
 * Po B1c vyrobi FLAT beh technicky report bytu SAM; jediny rucni vstup je to,
 * co stroj videt nemuze — jednotka z Nahlizeni do KN a lustrace vlastnika
 * v ISIR (rozhodnuti R.B. 25. 9. 2026). Formular je proto zamerne kratky
 * a zamerne bez volneho textu tam, kde jde o kod (typ jednotky, typ omezeni):
 * z kodu sklada vetu report, nikoli makler(ka).
 *
 * OSOBNI UDAJE (zadani 15f): formular NEMA pole pro jmeno vlastnika ani pro
 * rodne cislo. U ISIR se zadava POCET zaznamu a priznak, ze se lustrovalo
 * podle RC — nikdy jeho hodnota.
 *
 * ZADNY HARDCODED TEXT (zlate pravidlo 5): vsechno jde pres `webAdmin.unitCadastre.*`
 * a `dd.report.*`, cs je zdroj a en ma paritu.
 */

const cs = { locale: 'cs' } as const;

function tr(key: string, values?: Record<string, unknown>): string {
  return t(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

/** Typy omezeni z oddilu C listu vlastnictvi. Kody, texty i18n. */
const RESTRICTION_TYPES = ['LIEN', 'TRANSFER_BAN', 'EASEMENT', 'PREEMPTION', 'OTHER'] as const;
type RestrictionType = (typeof RESTRICTION_TYPES)[number];
/**
 * Krok K5 (vada 5): TYP zastavniho prava z oddilu C. Rucni A+ rozlisuje
 * „zastavni pravo smluvni" a rozdil meni postup prodeje (kvitance banky x
 * soucinnost exekutora). Nic se nepredvybira — nevyplneno znamena, ze report
 * vytiskne „zastavni pravo" bez typu, presne jako do K5.
 */
const LIEN_KINDS = ['CONTRACTUAL', 'STATUTORY', 'JUDICIAL', 'EXECUTORY'] as const;
type LienKind = (typeof LIEN_KINDS)[number];
const UNIT_KINDS = ['FLAT', 'NON_RESIDENTIAL', 'GARAGE', 'OTHER', 'UNKNOWN'] as const;
const DEFINITION_LAWS = ['NOZ', 'ZOVB', 'UNKNOWN'] as const;
/**
 * Krok B1d (vada 11): TYP vlastnika. `UNKNOWN` je vychozi a znamena „clovek to
 * nezadal" — fakt pak nevznikne a report poctive napise „Nezjisteno". Pole pro
 * jmeno ani rodne cislo tu NENI a nebude (15f).
 */
const OWNER_KINDS = ['UNKNOWN', 'NATURAL_PERSON', 'LEGAL_ENTITY', 'JOINT_MARITAL', 'MIXED'] as const;
/** Bydliste vlastnika v dome: „nevime" je jiny stav nez „ne". */
const RESIDES_OPTIONS = ['UNKNOWN', 'YES', 'NO'] as const;

interface FormState {
  lvNumber: string;
  shareNumerator: string;
  shareDenominator: string;
  unitKind: (typeof UNIT_KINDS)[number];
  definedUnder: (typeof DEFINITION_LAWS)[number];
  protection: string;
  restrictionTypes: readonly RestrictionType[];
  /** Krok K5 (vada 5): typ(y) zastavniho prava; prazdne = clovek je neopsal. */
  lienKinds: readonly LienKind[];
  /** Doložky po radcich — jedna doložka = jeden radek. */
  clauses: string;
  unitsOnSameLv: string;
  /** Krok B1d (vada 12): podily vsech jednotek na LV, jeden na radek. */
  sharesOnSameLv: string;
  ownerKind: (typeof OWNER_KINDS)[number];
  ownerCount: string;
  ownerResides: (typeof RESIDES_OPTIONS)[number];
  proceedingPriceCzk: string;
  /** Prazdno = lustrace jeste neprobehla; report o ni pak netvrdi nic. */
  insolvencyDebtors: string;
  insolvencyByBirthNumber: boolean;
}

const EMPTY: FormState = {
  lvNumber: '',
  shareNumerator: '',
  shareDenominator: '',
  unitKind: 'FLAT',
  definedUnder: 'UNKNOWN',
  protection: '',
  restrictionTypes: [],
  lienKinds: [],
  clauses: '',
  unitsOnSameLv: '',
  sharesOnSameLv: '',
  ownerKind: 'UNKNOWN',
  ownerCount: '',
  ownerResides: 'UNKNOWN',
  proceedingPriceCzk: '',
  insolvencyDebtors: '',
  insolvencyByBirthNumber: false,
};

/** Cele cislo, nebo `null`. Prazdne pole NENI nula — je to „nevime". */
function intOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 0 ? value : null;
}

const labelStyle: React.CSSProperties = { display: 'grid', gap: 2, fontSize: 'var(--rh-size-meta)' };
const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 10,
  marginBottom: 10,
};

export function UnitCadastreForm({
  caseId,
  onSaved,
}: {
  caseId: string;
  onSaved?: () => void;
}): React.JSX.Element {
  const [form, setForm] = React.useState<FormState>(EMPTY);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rules, setRules] = React.useState<readonly UnitCadastreRule[] | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const toggleRestriction = (type: RestrictionType): void => {
    setForm((previous) => ({
      ...previous,
      restrictionTypes: previous.restrictionTypes.includes(type)
        ? previous.restrictionTypes.filter((entry) => entry !== type)
        : [...previous.restrictionTypes, type],
    }));
  };

  const toggleLienKind = (kind: LienKind): void => {
    setForm((previous) => ({
      ...previous,
      lienKinds: previous.lienKinds.includes(kind)
        ? previous.lienKinds.filter((entry) => entry !== kind)
        : [...previous.lienKinds, kind],
    }));
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const debtors = intOrNull(form.insolvencyDebtors);
    const shares = form.sharesOnSameLv
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => /^\d+\/\d+$/.test(line));
    saveUnitCadastre(caseId, {
      lvNumber: form.lvNumber.trim() === '' ? null : form.lvNumber.trim(),
      shareNumerator: intOrNull(form.shareNumerator),
      shareDenominator: intOrNull(form.shareDenominator),
      unitKind: form.unitKind,
      definedUnder: form.definedUnder,
      protection: form.protection.trim() === '' ? null : form.protection.trim(),
      restrictionTypes: [...form.restrictionTypes],
      /**
       * Krok K5 (vada 5): typ zastavy se posila jen kdyz ho clovek opsal a jen
       * kdyz je zastava zaskrtnuta. Prazdne pole NENI prazdny seznam — „typ
       * neznáme" a „zastava zadny typ nema" jsou dve rozdilne veci.
       */
      ...(form.restrictionTypes.includes('LIEN') && form.lienKinds.length > 0
        ? { lienKinds: [...form.lienKinds] }
        : {}),
      clauses: form.clauses
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== ''),
      unitsOnSameLv: intOrNull(form.unitsOnSameLv),
      /**
       * Krok B1d (vada 12): podily se posilaji jen kdyz je clovek opsal.
       * Prazdne pole NENI prazdny seznam — report z nej jinak spocita
       * „spolecne 0 % domu", coz je nepravda, ne chybejici udaj.
       */
      ...(shares.length === 0 ? {} : { sharesOnSameLv: shares }),
      /** Krok B1d (vada 11): typ vlastnika, nikdy jmeno. */
      ...(form.ownerKind === 'UNKNOWN'
        ? {}
        : {
            ownerKind: form.ownerKind,
            ownerCount: intOrNull(form.ownerCount),
            ownerResidesInBuilding:
              form.ownerResides === 'UNKNOWN' ? null : form.ownerResides === 'YES',
          }),
      proceedingPriceCzk: intOrNull(form.proceedingPriceCzk),
      /**
       * Lustrace, kterou clovek neudelal, se NEPOSILA. `0 dluzniku` je odpoved
       * „ne", prazdne pole je „nevime" — report je nesmi slucovat.
       */
      ownerInsolvency:
        debtors === null
          ? null
          : {
              debtorsFound: debtors,
              queriedOn: null,
              byBirthNumber: form.insolvencyByBirthNumber,
            },
    }).then(
      (result) => {
        /** Pravidla z LV prepocitava server; UI je jen ukazuje (zadani bod 9). */
        setRules(result.rules);
        setBusy(false);
        onSaved?.();
      },
      (cause: unknown) => {
        setError(cause instanceof Error ? cause.message : String(cause));
        setBusy(false);
      },
    );
  };

  return (
    <Card
      heading={tr('webAdmin.unitCadastre.heading')}
      hint={tr('webAdmin.unitCadastre.hint')}
      testId="unit-cadastre"
    >
      <form onSubmit={submit} data-testid="unit-cadastre-form">
        <div style={rowStyle}>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.lvNumber')}
            <input
              data-testid="unit-lv"
              value={form.lvNumber}
              onChange={(event) => set('lvNumber', event.target.value)}
            />
          </label>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.shareNumerator')}
            <input
              data-testid="unit-share-numerator"
              inputMode="numeric"
              value={form.shareNumerator}
              onChange={(event) => set('shareNumerator', event.target.value)}
            />
          </label>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.shareDenominator')}
            <input
              data-testid="unit-share-denominator"
              inputMode="numeric"
              value={form.shareDenominator}
              onChange={(event) => set('shareDenominator', event.target.value)}
            />
          </label>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.unitsOnSameLv')}
            <input
              data-testid="unit-units-on-lv"
              inputMode="numeric"
              value={form.unitsOnSameLv}
              onChange={(event) => set('unitsOnSameLv', event.target.value)}
            />
          </label>
        </div>

        <div style={rowStyle}>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.unitKind')}
            <select
              data-testid="unit-kind"
              value={form.unitKind}
              onChange={(event) => set('unitKind', event.target.value as FormState['unitKind'])}
            >
              {UNIT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {tr(`webAdmin.unitCadastre.kind.${kind}`)}
                </option>
              ))}
            </select>
          </label>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.definedUnder')}
            <select
              data-testid="unit-defined-under"
              value={form.definedUnder}
              onChange={(event) =>
                set('definedUnder', event.target.value as FormState['definedUnder'])
              }
            >
              {DEFINITION_LAWS.map((law) => (
                <option key={law} value={law}>
                  {tr(`webAdmin.unitCadastre.law.${law}`)}
                </option>
              ))}
            </select>
          </label>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.protection')}
            <input
              data-testid="unit-protection"
              value={form.protection}
              onChange={(event) => set('protection', event.target.value)}
            />
          </label>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.proceedingPrice')}
            <input
              data-testid="unit-proceeding-price"
              inputMode="numeric"
              value={form.proceedingPriceCzk}
              onChange={(event) => set('proceedingPriceCzk', event.target.value)}
            />
          </label>
        </div>

        <fieldset style={{ border: '1px solid var(--rh-border)', borderRadius: 6, marginBottom: 10 }}>
          <legend style={{ fontSize: 'var(--rh-size-meta)' }}>{tr('webAdmin.unitCadastre.restrictions')}</legend>
          {RESTRICTION_TYPES.map((type) => (
            <label key={type} style={{ marginRight: 12, fontSize: 'var(--rh-size-meta)' }}>
              <input
                type="checkbox"
                data-testid={`unit-restriction-${type}`}
                checked={form.restrictionTypes.includes(type)}
                onChange={() => toggleRestriction(type)}
              />{' '}
              {tr(`dd.report.claim.UNIT_BURDENS`, { type, lienKind: 'UNKNOWN' })}
            </label>
          ))}
          {form.restrictionTypes.includes('LIEN') ? (
            <div data-testid="unit-lien-kinds" style={{ marginTop: 6, fontSize: 'var(--rh-size-meta)' }}>
              <span style={{ marginRight: 8 }}>{tr('webAdmin.unitCadastre.lienKinds')}</span>
              {LIEN_KINDS.map((kind) => (
                <label key={kind} style={{ marginRight: 12 }}>
                  <input
                    type="checkbox"
                    data-testid={`unit-lien-kind-${kind}`}
                    checked={form.lienKinds.includes(kind)}
                    onChange={() => toggleLienKind(kind)}
                  />{' '}
                  {tr(`webAdmin.unitCadastre.lienKindOption.${kind}`)}
                </label>
              ))}
            </div>
          ) : null}
        </fieldset>

        <div style={rowStyle}>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.ownerKind')}
            <select
              data-testid="unit-owner-kind"
              value={form.ownerKind}
              onChange={(event) => set('ownerKind', event.target.value as FormState['ownerKind'])}
            >
              {OWNER_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {tr(`webAdmin.unitCadastre.ownerKindOption.${kind}`)}
                </option>
              ))}
            </select>
          </label>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.ownerCount')}
            <input
              data-testid="unit-owner-count"
              inputMode="numeric"
              value={form.ownerCount}
              onChange={(event) => set('ownerCount', event.target.value)}
            />
          </label>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.ownerResides')}
            <select
              data-testid="unit-owner-resides"
              value={form.ownerResides}
              onChange={(event) =>
                set('ownerResides', event.target.value as FormState['ownerResides'])
              }
            >
              {RESIDES_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {tr(`webAdmin.unitCadastre.residesOption.${option}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label style={{ ...labelStyle, marginBottom: 10 }}>
          {tr('webAdmin.unitCadastre.sharesOnSameLv')}
          <textarea
            data-testid="unit-shares-on-lv"
            rows={2}
            value={form.sharesOnSameLv}
            onChange={(event) => set('sharesOnSameLv', event.target.value)}
          />
        </label>

        <label style={{ ...labelStyle, marginBottom: 10 }}>
          {tr('webAdmin.unitCadastre.clauses')}
          <textarea
            data-testid="unit-clauses"
            rows={3}
            value={form.clauses}
            onChange={(event) => set('clauses', event.target.value)}
          />
        </label>

        <div style={rowStyle}>
          <label style={labelStyle}>
            {tr('webAdmin.unitCadastre.insolvencyDebtors')}
            <input
              data-testid="unit-insolvency-debtors"
              inputMode="numeric"
              value={form.insolvencyDebtors}
              onChange={(event) => set('insolvencyDebtors', event.target.value)}
            />
          </label>
          <label style={{ ...labelStyle, alignSelf: 'end' }}>
            <span>
              <input
                type="checkbox"
                data-testid="unit-insolvency-by-birth-number"
                checked={form.insolvencyByBirthNumber}
                onChange={(event) => set('insolvencyByBirthNumber', event.target.checked)}
              />{' '}
              {tr('webAdmin.unitCadastre.insolvencyByBirthNumber')}
            </span>
          </label>
        </div>

        <button type="submit" disabled={busy} data-testid="unit-cadastre-save">
          {tr('webAdmin.unitCadastre.save')}
        </button>
      </form>

      {rules !== null && (
        <div data-testid="unit-cadastre-rules" style={{ marginTop: 10, fontSize: 'var(--rh-size-meta)' }}>
          <strong>{tr('dd.report.flat.rules.title')}</strong>
          <ul style={{ margin: '4px 0 0 18px' }}>
            {rules.map((rule) => (
              <li key={rule.code} data-rule={rule.code}>
                {tr(`dd.report.rule.${rule.code}`)}
              </li>
            ))}
          </ul>
          {rules.length === 0 && <p style={{ margin: 0 }}>{tr('webAdmin.unitCadastre.noRules')}</p>}
        </div>
      )}

      {error !== null && (
        <p role="alert" style={{ marginTop: 8, fontSize: 'var(--rh-size-meta)', color: 'var(--rh-danger)' }}>
          {error}
        </p>
      )}
    </Card>
  );
}
